import React, { useState, useEffect } from 'react';
import { useUser } from '../contexts/UserContext';
import { useLanguage, useTranslation } from '../i18n';
import { apiClient } from '../services/api';
import { getCategoryIcon } from '../data/mockData';
import type { Transaction } from '../data/mockData';
import { BuyCrystalsModal } from './BuyCrystalsModal';
import './BalancePage.css';

export const BalancePage: React.FC = () => {
  const { user, refreshUser } = useUser();
  const { language } = useLanguage();
  const t = useTranslation();
  const [filter, setFilter] = useState<'all' | 'income' | 'expense'>('all');
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [loading, setLoading] = useState(true);
  const [isBuyModalOpen, setIsBuyModalOpen] = useState(false);

  useEffect(() => {
    loadTransactions();
  }, []);

  const loadTransactions = async () => {
    try {
      setLoading(true);
      const response = await apiClient.getTransactions();
      setTransactions(response.data);
    } catch (error) {
      console.error('Failed to load transactions:', error);
    } finally {
      setLoading(false);
    }
  };

  const filteredTransactions = filter === 'all' 
    ? transactions 
    : transactions.filter(t => t.type === filter);

  const handlePurchaseSuccess = () => {
    // Reload transactions and user data after successful payment initiation
    loadTransactions();
    refreshUser();
  };

  const getLocale = () => {
    switch (language) {
      
      
      default: return 'es-ES';
    }
  };

  const formatDate = (dateString: string): string => {
    const date = new Date(dateString);
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);

    if (date.toDateString() === today.toDateString()) {
      return t.common.today;
    } else if (date.toDateString() === yesterday.toDateString()) {
      return t.common.yesterday;
    } else {
      return date.toLocaleDateString(getLocale(), { day: 'numeric', month: 'long' });
    }
  };

  if (loading) {
    return (
      <div className="page-container balance-page">
        <div style={{ textAlign: 'center', padding: '40px' }}>
          <p>{t.common.loading}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="page-container balance-page">
      <div className="balance-header">
        <h1 className="page-title">{t.balancePage.title}</h1>
        <div className="balance-card">
          <div className="balance-label">{t.balancePage.yourBalance}</div>
          <div className="balance-value">
            <span className="balance-amount">{(user as any)?.balance || user?.game_stats?.total_points || 0}</span>
            <span className="balance-icon">💎</span>
          </div>
          <button 
            className="buy-crystals-btn"
            onClick={() => setIsBuyModalOpen(true)}
          >
            💎 Пополнить баланс
          </button>
        </div>
      </div>

      <div className="transactions-section">
        <div className="transactions-header">
          <h2 className="section-title">{t.balancePage.transactionHistory}</h2>
          <div className="filter-buttons">
            <button 
              className={`filter-btn ${filter === 'all' ? 'active' : ''}`}
              onClick={() => setFilter('all')}
            >
              {t.common.all}
            </button>
            <button 
              className={`filter-btn ${filter === 'income' ? 'active' : ''}`}
              onClick={() => setFilter('income')}
            >
              {t.balancePage.income}
            </button>
            <button 
              className={`filter-btn ${filter === 'expense' ? 'active' : ''}`}
              onClick={() => setFilter('expense')}
            >
              {t.balancePage.expense}
            </button>
          </div>
        </div>

        <div className="transactions-list">
          {filteredTransactions.map((transaction: any) => (
            <TransactionCard key={transaction.id} transaction={transaction} formatDate={formatDate} />
          ))}
        </div>

        {filteredTransactions.length === 0 && (
          <div className="empty-state">
            <p>{t.balancePage.noTransactions}</p>
          </div>
        )}
      </div>

      <BuyCrystalsModal
        isOpen={isBuyModalOpen}
        onClose={() => setIsBuyModalOpen(false)}
        onSuccess={handlePurchaseSuccess}
      />
    </div>
  );
};

interface TransactionCardProps {
  transaction: Transaction;
  formatDate: (dateString: string) => string;
}

const TransactionCard: React.FC<TransactionCardProps> = ({ transaction, formatDate }) => {
  return (
    <div className="transaction-card">
      <div className="transaction-icon">
        <span className="icon-emoji">{getCategoryIcon(transaction.category)}</span>
      </div>
      <div className="transaction-info">
        <div className="transaction-description">{transaction.description}</div>
        <div className="transaction-date">{formatDate(transaction.date)}</div>
      </div>
      <div className={`transaction-amount ${transaction.type}`}>
        {transaction.type === 'income' ? '+' : ''}{transaction.amount}
      </div>
    </div>
  );
};
